<template>
  <div class="beds-modern">
    <NavBar />
    <AuthBySessionExpired />
    <new-bed @created="getBeds" />
    <view-bed :bed="bed" @updated="getBeds" @close="bed = null" />

    <div class="beds-container">
      <!-- Header Section -->
      <div class="beds-header">
        <div class="header-content">
          <div class="back-button-section">
            <v-btn class="btn-back-modern" @click="handleToBack">
              <i class="fas fa-arrow-left mr-2"></i>
              Voltar
            </v-btn>
          </div>

          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-bed title-icon"></i>
              Leitos
            </h1>
            <p class="page-subtitle">Gerencie os leitos da instituição</p>
          </div>

          <div class="actions-section">
            <v-btn
              v-if="permission('write', 'bed')"
              class="btn-add-modern"
              @click="$bvModal.show('new_bed')"
            >
              <i class="fas fa-plus mr-2"></i>
              Adicionar
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="beds-content">
        <!-- Filters Section -->
        <div class="filters-section">
          <div class="filters-card">
            <div class="filters-grid">
              <!-- Quarto Filter -->
              <div class="filter-group">
                <label class="filter-label">
                  <i class="fas fa-door-open filter-icon"></i>
                  Quarto
                </label>
                <v-text-field
                  v-model="search_bedroom"
                  placeholder="Pesquisar quarto"
                  outlined
                  dense
                  hide-details
                  class="modern-input"
                  @input="handleFilterBeds"
                />
              </div>

              <!-- Número Filter -->
              <div class="filter-group">
                <label class="filter-label">
                  <i class="fas fa-hashtag filter-icon"></i>
                  Número
                </label>
                <v-text-field
                  v-model="search_number"
                  placeholder="Pesquisar número"
                  outlined
                  dense
                  hide-details
                  class="modern-input"
                  @input="handleFilterBeds"
                />
              </div>

              <!-- Andar Filter -->
              <div class="filter-group">
                <label class="filter-label">
                  <i class="fas fa-building filter-icon"></i>
                  Andar
                </label>
                <v-text-field
                  v-model="search_walk"
                  placeholder="Pesquisar andar"
                  outlined
                  dense
                  hide-details
                  class="modern-input"
                  @input="handleFilterBeds"
                />
              </div>

              <!-- Ala/Setor Filter -->
              <div class="filter-group">
                <label class="filter-label">
                  <i class="fas fa-map-marked-alt filter-icon"></i>
                  Ala/Setor
                </label>
                <v-text-field
                  v-model="search_ward_or_sector"
                  placeholder="Pesquisar ala/setor"
                  outlined
                  dense
                  hide-details
                  class="modern-input"
                  @input="handleFilterBeds"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Data Table Section -->
        <div class="table-section">
          <div class="table-card">
            <b-overlay
              :show="loadingBed"
              opacity="0.99"
              class="loading-overlay"
            >
              <!-- No Results -->
              <div v-show="beds.length == 0 && !loadingBed" class="no-results">
                <i class="fas fa-bed no-results-icon"></i>
                <h3 class="no-results-title">Nenhum leito encontrado</h3>
                <p class="no-results-text">
                  Tente ajustar os filtros ou adicione um novo leito
                </p>
              </div>

              <!-- Data Table -->
              <v-data-table
                v-show="beds.length > 0"
                v-if="permission('read', 'bed')"
                :headers="headers"
                :items="beds"
                :footer-props="{
                  'items-per-page-options': [50, 75, 100],
                  'items-per-page-text': 'Mostrar',
                }"
                class="modern-table"
                :no-data-text="'Nenhum leito encontrado'"
                :loading-text="'Carregando leitos...'"
              >
                <!-- Ocupado Column -->
                <template v-slot:[`item.resident_name`]="{ item }">
                  <div class="occupancy-cell">
                    <span
                      v-if="item.resident_name"
                      class="status-badge occupied"
                    >
                      <i class="fas fa-user-check mr-1"></i>
                      {{ item.resident_name }}
                    </span>
                    <span v-else class="status-badge available">
                      <i class="fas fa-bed mr-1"></i>
                      Disponível
                    </span>
                  </div>
                </template>

                <!-- Actions Column -->
                <template v-slot:[`item.actions`]="{ item }">
                  <v-btn class="btn-action-edit" small @click="bed = item">
                    <i class="fas fa-edit mr-1"></i>
                    Editar
                  </v-btn>
                </template>
              </v-data-table>

              <!-- Error Messages -->
              <ErrorsMessages :errors="errors" />
            </b-overlay>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from "../templates/NavBar";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import ErrorsMessages from "../templates/Components/ErrorsMessages";
import routes from "../routes";
import api from "../services/api";
import permission from "../lib/permission";
import { userData } from "../lib/functions";
import NewBed from "../templates/beds/new-bed.vue";
import ViewBed from "../templates/beds/view-bed.vue";

export default {
  name: "Beds",
  components: {
    NavBar,
    AuthBySessionExpired,
    ErrorsMessages,
    NewBed,
    ViewBed,
  },
  data() {
    return {
      permissions: {},
      search_bedroom: "",
      search_number: "",
      search_walk: "",
      search_ward_or_sector: "",
      headers: [
        { text: "STATUS/RESIDENTE", value: "resident_name" },
        { text: "NÚMERO", value: "number" },
        { text: "ANDAR", value: "walk" },
        { text: "ALA/SETOR", value: "ward_or_sector" },
        { text: "QUARTO", value: "bedroom" },
        { text: "AÇÕES", value: "actions", sortable: false },
      ],
      rest_home_id: "",
      bed: null,
      beds: [],
      loadingBed: true,
      errors: [],
    };
  },
  created() {
    this.retrieve();
  },
  methods: {
    permission,

    handleToBack: () => routes.go(-1),

    async handleFilterBeds() {
      try {
        this.loadingBed = true;

        const {
          rest_home_id,
          search_bedroom: bedroom,
          search_number: number,
          search_walk: walk,
          search_ward_or_sector: ward_or_sector,
        } = this;

        let params = {
          rest_home_id,
          bedroom,
          number,
          walk,
          ward_or_sector,
        };

        const response = await api.get("/rest-home/bed/filter", {
          params,
        });

        const { error, errors, beds } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.beds = beds;
        }

        this.loadingBed = false;
      } catch (error) {
        console.log(error);
      }
    },

    retrieve() {
      this.permissions = userData().permissions;
      this.rest_home_id = userData().user.rest_home_id;

      this.getBeds();
    },

    async getBeds(page = 1) {
      try {
        this.loadingBed = true;

        const { rest_home_id } = this;

        let params = {
          rest_home_id,
          page,
        };

        const response = await api.get("/rest-home/bed/paginate", {
          params,
        });

        const { error, errors, beds } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.beds = beds;
        }
        this.loadingBed = false;
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
.beds-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.beds-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header Section */
.beds-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

/* Buttons */
.btn-back-modern {
  background: transparent !important;
  color: #64748b !important;
  border: 2px solid #e5e7eb !important;
  border-radius: 12px !important;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
  transition: all 0.3s ease !important;
}

.btn-back-modern:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.btn-add-modern {
  background: linear-gradient(135deg, #4e9130 0%, #1e884d 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(78, 145, 48, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-add-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(78, 145, 48, 0.4) !important;
}

/* Content Section */
.beds-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Filters Section */
.filters-section {
  margin-bottom: 8px;
}

.filters-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.filter-icon {
  color: #6b7280;
  font-size: 14px;
}

/* Table Section */
.table-section {
  margin-bottom: 32px;
}

.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.loading-overlay {
  border-radius: 16px;
  min-height: 400px;
}

/* No Results */
.no-results {
  text-align: center;
  padding: 80px 24px;
}

.no-results-icon {
  font-size: 64px;
  color: #cbd5e1;
  margin-bottom: 20px;
}

.no-results-title {
  color: #1e293b;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.no-results-text {
  color: #64748b;
  font-size: 16px;
  margin: 0;
}

/* Table Styling */
.modern-table {
  background: transparent !important;
}

.occupancy-cell {
  display: flex;
  align-items: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.occupied {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

.status-badge.available {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.btn-action-edit {
  background: linear-gradient(135deg, #1d75be 0%, #155a8a 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 12px !important;
  box-shadow: 0 2px 8px rgba(29, 117, 190, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-action-edit:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(29, 117, 190, 0.4) !important;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .header-content {
    grid-template-columns: 1fr;
    gap: 16px;
    text-align: center;
  }

  .page-title {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .beds-container {
    padding: 0 16px;
  }

  .beds-header {
    padding: 16px 0;
    margin-bottom: 24px;
  }

  .header-content {
    padding: 0 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .filters-card,
  .table-card {
    border-radius: 12px;
    padding: 16px;
  }

  .filters-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

@media (max-width: 480px) {
  .btn-back-modern,
  .btn-add-modern {
    padding: 10px 16px !important;
    font-size: 14px !important;
  }

  .page-title {
    font-size: 20px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .no-results-icon {
    font-size: 48px;
  }

  .no-results-title {
    font-size: 18px;
  }

  .no-results-text {
    font-size: 14px;
  }
}

/* Override Vuetify styles */
.modern-input >>> .v-input__control {
  border-radius: 8px !important;
}

.modern-input >>> .v-text-field__slot input {
  font-weight: 500 !important;
  padding: 12px 0 !important;
}

.modern-table >>> .v-data-table__wrapper {
  border-radius: 0 !important;
}

.modern-table >>> .v-data-table thead th {
  background-color: #f8fafc !important;
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e5e7eb !important;
}

.modern-table >>> .v-data-table tbody tr:hover {
  background-color: #f8fafc !important;
}
</style>